/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: 应用主页，展示当周运动时长摘要及用户偏好的运动项目卡片
 * Style referenceFiles:
 * Design for: Mobile
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - 查看当周有氧/无氧运动总时长
 * - 点击运动项目卡片进入具体运动页面
 * - 无运动项目时，点击引导按钮前往设置页
 * - 导航: 爬楼梯 (push), 平板支撑 (push), 哑铃弯举 (push), 设置 (Tab)
 *
 * ## Basic Layout
 * Tab-based: Header + Scrollable Main + Pinned Bottom
 *
 * ## Reuse Components
 * - `TabBar`: 底部导航
 *
 * ## Page Layout
 * `flex flex-col h-screen bg-gray-50`
 *
 * **Header**: nav-bar - [none] - [黑洞健身 首页] - [none]
 *
 * **Main** `flex-1 overflow-y-auto p-4 flex flex-col gap-6`:
 * 1. 当周摘要 (Stats Board): `flex flex-row justify-between bg-white p-4 rounded-xl shadow-sm`
 *    - 有氧模块: `flex flex-col items-center gap-1 w-1/2 border-r` -> 标题 "当周有氧" + 值 "120" `text-2xl font-bold text-blue-500` + 单位 "分钟"
 *    - 无氧模块: `flex flex-col items-center gap-1 w-1/2` -> 标题 "当周无氧" + 值 "45" `text-2xl font-bold text-purple-500` + 单位 "分钟"
 * 2. 运动项目列表: `flex flex-col gap-3` (如果用户已添加运动)
 *    - 标题 "我的运动" `text-lg font-bold`
 *    - grid-2col 布局，展示运动卡片 `flex flex-col bg-white p-4 rounded-xl shadow-sm items-center justify-center gap-2` -> 运动Icon + 运动名称
 * 3. 空状态提示 (如果无运动项目): `flex flex-col items-center justify-center py-10 gap-4 text-gray-500`
 *    - 提示文案 "暂无运动项目，请前往设置添加"
 *    - 引导按钮 "去设置添加" `px-4 py-2 bg-blue-50 text-blue-600 rounded-lg`
 *
 * **Bottom**: `TabBar` - 选中项 "首页"
 *
 * ## Mock Data
 * 有氧 120分钟，无氧 45分钟。
 * 卡片列表：爬楼梯、平板支撑、哑铃弯举。
 *
 * ## Reference
 * - canvas/[Feature] Home/feature-brief.md
 * </page-design>
 */

import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { pageLinks } from '../../pageLinks'
import { TabBar } from '../../components/function/TabBar'
import * as HomeStore from '../../stores/HomeStore'
import * as SettingsStore from '../../stores/SettingsStore'
import type { AnaerobicBreakdownItem, HomeGroupKey } from '../../stores/HomeStore'
import { CATEGORY_DISPLAY } from '@/lib/exerciseRegistry'

const HOME_GROUP_DISPLAY: Array<{ key: HomeGroupKey; label: string }> = [
  ...CATEGORY_DISPLAY,
  { key: 'custom', label: '自定义' },
]

function summariseAnaerobic(items: AnaerobicBreakdownItem[]): string {
  let sets = 0
  let reps = 0
  for (const it of items) {
    sets += it.sets ?? 0
    reps += it.reps ?? 0
  }
  const parts: string[] = []
  if (sets > 0) parts.push(`${sets} 组`)
  if (reps > 0) parts.push(`${reps} 次`)
  return parts.join(' ')
}

// Per-exercise breakdown used for today's section.
function formatAnaerobicParts(items: AnaerobicBreakdownItem[]) {
  const parts: string[] = []
  for (const it of items) {
    const segs: string[] = []
    if (it.sets && it.sets > 0) segs.push(`${it.sets} 组`)
    if (it.reps && it.reps > 0) segs.push(`${it.reps} 次`)
    if (it.minutes && it.minutes > 0) segs.push(`${it.minutes} 分钟`)
    if (segs.length > 0) parts.push(`${it.label} ${segs.join(' ')}`)
  }
  return parts.join('，')
}

// Beijing-time weekday key for the current moment ('mon'..'sun')
function todayWeekdayKey(): 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun' {
  const bj = new Date(Date.now() + 8 * 60 * 60 * 1000)
  const dow = bj.getUTCDay() // 0=Sun..6=Sat
  return (['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const)[dow]
}

function Home() {
  const exercises = useAtomValue(HomeStore.exercisesAtom)
  const weeklyStats = useAtomValue(HomeStore.weeklyStatsAtom)
  const todayStats = useAtomValue(HomeStore.todayStatsAtom)
  const todayDate = useAtomValue(HomeStore.todayDateAtom)
  const { data: weeklyPlan } = useAtomValue(SettingsStore.weeklyPlanAtom)
  const removeExercise = useSetAtom(SettingsStore.removeExerciseAtom)
  const navigate = useNavigate()
  const [isManaging, setIsManaging] = useState(false)

  const weeklyAnaerobicText = summariseAnaerobic(weeklyStats.anaerobic.items)
  const todayAnaerobicText = formatAnaerobicParts(todayStats.anaerobic.items)
  const todayPlanText = weeklyPlan ? weeklyPlan[todayWeekdayKey()] : null

  return (
    <div className="flex flex-col h-screen relative" style={{ backgroundColor: '#12141D' }}>
      {/* Header */}
      <div className="flex-none pt-12 pb-4 px-6 flex items-center gap-3">
        <img src="/logo.png" alt="黑洞健身" className="w-9 h-9 rounded-xl" />
        <h1 className="text-2xl font-bold tracking-tight text-white">黑洞健身</h1>
        <button
          onClick={() => navigate(pageLinks.MonthlyCalendar())}
          aria-label="月度活动"
          className="ml-auto w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#1C1F2E' }}
        >
          <Icon icon="lucide:calendar-days" className="w-5 h-5 text-emerald-400" />
        </button>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto px-6 pb-32 flex flex-col gap-5">
        {/* Stats Board */}
        <section
          className="rounded-3xl px-5 py-4 flex flex-col gap-2 cursor-pointer active:opacity-80 transition-opacity"
          style={{ backgroundColor: '#1C1F2E', borderBottom: '4px solid #151722' }}
          onClick={() => navigate(pageLinks.WeeklyDetail())}
        >
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-bold text-gray-300">数据统计</h2>
            <div className="flex items-center gap-2">
              {todayDate && <span className="text-xs text-gray-500 font-mono">{todayDate}</span>}
              <Icon icon="lucide:chevron-right" className="w-4 h-4 text-gray-500" />
            </div>
          </div>
          <p className="text-sm text-gray-400 leading-relaxed">
            <span className="text-emerald-400 font-medium">本周有氧</span>{' '}
            <span className="text-white font-bold">{weeklyStats.aerobicMinutes}</span> 分钟
            {weeklyAnaerobicText && (
              <>
                ，<span className="text-violet-400 font-medium">本周无氧</span>{' '}
                <span className="text-white font-bold">{weeklyAnaerobicText}</span>
              </>
            )}
          </p>
          {todayStats.aerobicMinutes > 0 && (
            <p className="text-sm text-gray-400 leading-relaxed">
              <span className="text-emerald-400 font-medium">今天有氧</span>{' '}
              <span className="text-white font-bold">{todayStats.aerobicMinutes}</span> 分钟
            </p>
          )}
          {todayAnaerobicText && (
            <p className="text-sm text-gray-400 leading-relaxed">
              <span className="text-violet-400 font-medium">今天无氧</span>：{todayAnaerobicText}
            </p>
          )}
        </section>

        {/* Today's plan — only shown if there's text for the current weekday */}
        {todayPlanText && (
          <section
            className="rounded-2xl px-5 py-3"
            style={{ backgroundColor: '#1C1F2E' }}
          >
            <p className="text-sm text-gray-300 leading-relaxed">
              <span className="text-amber-300 font-medium">今日计划：</span>
              <span className="text-white">{todayPlanText}</span>
            </p>
          </section>
        )}

        {/* Exercises */}
        {exercises.length > 0 ? (
          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-lg font-bold text-gray-300">我的运动</h2>
              <button
                onClick={() => setIsManaging(!isManaging)}
                aria-label={isManaging ? '完成' : '管理'}
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: isManaging ? '#10B981' : '#1C1F2E' }}
              >
                <Icon
                  icon={isManaging ? 'lucide:check' : 'lucide:pencil-line'}
                  className={`w-4 h-4 ${isManaging ? 'text-white' : 'text-gray-400'}`}
                />
              </button>
            </div>
            {HOME_GROUP_DISPLAY.map((group) => {
              const items = exercises.filter((ex) => ex.groupKey === group.key)
              if (items.length === 0) return null
              return (
                <div key={group.key} className="flex flex-col gap-2">
                  <h3 className="text-xs font-bold text-gray-500 px-1 uppercase tracking-wider">{group.label}</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {items.map((ex) => {
                      const iconClasses = ex.color === 'mint'
                        ? 'bg-emerald-400/10 text-emerald-400'
                        : 'bg-violet-400/10 text-violet-400'
                      const handleClick = () => {
                        if (isManaging) {
                          void removeExercise(ex.exerciseKey)
                        } else {
                          navigate(ex.path)
                        }
                      }
                      return (
                        <button
                          key={ex.id}
                          onClick={handleClick}
                          className="relative flex items-center gap-2.5 px-3 py-2.5 rounded-2xl border-b-[3px] active:border-b-0 active:translate-y-[3px] transition-all min-w-0"
                          style={{ backgroundColor: '#1C1F2E', borderColor: '#151722' }}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-none ${iconClasses}`}>
                            <Icon icon={ex.icon} className="w-4 h-4" />
                          </div>
                          <span className="text-sm font-bold text-gray-200 truncate min-w-0 text-left flex-1">{ex.label}</span>
                          {isManaging && (
                            <div className="w-5 h-5 rounded-full flex items-center justify-center bg-red-500 flex-none">
                              <Icon icon="lucide:minus" className="w-3.5 h-3.5 text-white" />
                            </div>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </section>
        ) : (
          <section className="flex flex-col items-center justify-center py-16 gap-5">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#1C1F2E' }}
            >
              <Icon icon="lucide:dumbbell" className="w-10 h-10 text-gray-600" />
            </div>
            <p className="text-gray-500 font-medium">暂无运动项目，请前往设置添加</p>
            <button
              onClick={() => navigate(pageLinks.Settings())}
              className="px-6 py-3 rounded-2xl font-bold text-emerald-400 border-b-[4px] active:border-b-0 active:translate-y-[4px] transition-all"
              style={{ backgroundColor: '#1C1F2E', borderColor: '#151722' }}
            >
              去设置添加
            </button>
          </section>
        )}
      </main>

      {/* TabBar */}
      <TabBar />
    </div>
  )
}

export default Home
